import IconCollapseList from '@apollo/icons/default/IconCollapseList.svg';
import { Button, ButtonGroup } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useState } from 'react';

import { insightsRouteConfig } from 'src/app/graph/routes';
import { useOperationInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';

import { ColorSwatch } from './ColorSwatch';
import { getRouteParamForGroupOption } from './getRouteParam';
import { GroupOption } from './GroupByButton';

interface LegendItem {
  /**
   * The legend item's title
   */
  title: string;

  /**
   * The dimension value for this item
   */
  value: string;

  /**
   * The legend item's color swatch
   */
  color: string;

  /**
   * Controls whether the legend item is displayed in a selected way.
   */
  selected: boolean;
}

/**
 * Renders the error group legend.
 *
 * @param children Expecting a list of LegendItems.
 * @returns {JSX.Element} A rendered view of the items as buttons.
 */
function Legend({
  items,
  loading,
  groupByDimension,
}: {
  className?: string;
  items: LegendItem[];
  loading?: boolean;
  groupByDimension: GroupOption;
}) {
  const [visibleCount, setVisibleCount] = useState(9);
  const initialCount = 9;

  const trackCustomEvent = useTrackCustomEvent();

  const { setFilterParams } = useOperationInsightsFilter();
  const handleItemClick = useCallback(
    (value: string) => {
      trackCustomEvent({
        category: 'Operation Insights Errors',
        action: 'legend_item_click',
      });

      const errorsFilter = {
        [getRouteParamForGroupOption(groupByDimension)]: value,
      };
      setFilterParams(insightsRouteConfig, errorsFilter);
    },
    [groupByDimension, setFilterParams, trackCustomEvent],
  );

  const handleShowMore = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'legend_show_more_click',
    });
    setVisibleCount((prev) => Math.min(prev + 10, items.length));
  };

  const handleShowLess = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'legend_show_less_click',
    });
    setVisibleCount(initialCount);
  };

  return (
    <div className="relative">
      <ButtonGroup
        isDisabled={loading}
        className={classnames('mb-6 flex-wrap gap-2', {
          'opacity-40': loading,
        })}
        spacing="0"
      >
        {items.slice(0, visibleCount).map((item) => (
          <Item key={item.title} item={item} onClick={handleItemClick} />
        ))}
        {items.length > visibleCount && (
          <Button variant="secondary" size="sm" onClick={handleShowMore}>
            +{items.length - visibleCount} more
          </Button>
        )}
        {visibleCount > initialCount && (
          <Button
            variant="buttonLink"
            size="sm"
            leftIcon={<IconCollapseList />}
            onClick={handleShowLess}
          >
            Show less
          </Button>
        )}
      </ButtonGroup>
    </div>
  );
}

function Item({
  item,
  onClick,
}: {
  item: LegendItem;
  onClick: (value: string) => void;
}) {
  return (
    <Button
      variant="secondary"
      size="sm"
      className={classnames({ 'text-disabled': !item.selected })}
      leftIcon={<ColorSwatch color={item.color} isMuted={!item.selected} />}
      onClick={() => onClick(item.value)}
    >
      {item.title}
    </Button>
  );
}

export { Legend, LegendItem };
